import React from "react";
import { ArrowRight, Car, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AuthViewModelReturn } from "@/viewmodels/auth/useAuthViewModel";

interface RegistrationStepProps {
  vm: AuthViewModelReturn;
  t: (key: string, namespace?: string) => string;
}

/** Shared new-user registration step used by the login and booking auth flows. */
export const RegistrationStep: React.FC<RegistrationStepProps> = ({ vm, t }) => {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Complete Your Profile
        </h3>
        <p className="text-gray-600">Tell us a bit about yourself</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="firstName">{t("firstName", "aboutYou")}</Label>
          <input
            id="firstName"
            type="text"
            placeholder={t("phFirstName", "aboutYou")}
            value={vm.firstName}
            onChange={(e) => vm.setFirstName(e.target.value)}
            onBlur={() => vm.validateFirstName()}
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${
              vm.firstNameError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-blue-500"
            }`}
          />
          {vm.firstNameError && (
            <p className="mt-1 text-sm text-red-600">{vm.firstNameError}</p>
          )}
        </div>
        <div>
          <Label htmlFor="lastName">{t("lastName", "aboutYou")}</Label>
          <input
            id="lastName"
            type="text"
            placeholder={t("phLastName", "aboutYou")}
            value={vm.lastName}
            onChange={(e) => vm.setLastName(e.target.value)}
            onBlur={() => vm.validateLastName()}
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${
              vm.lastNameError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-blue-500"
            }`}
          />
          {vm.lastNameError && (
            <p className="mt-1 text-sm text-red-600">{vm.lastNameError}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label>{t("gender", "aboutYou")}</Label>
          <Select value={vm.gender} onValueChange={vm.setGender}>
            <SelectTrigger>
              <SelectValue placeholder={t("gender", "aboutYou")} />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="Male">{t("male", "aboutYou")}</SelectItem>
              <SelectItem value="Female">{t("female", "aboutYou")}</SelectItem>
              <SelectItem value="Other">{t("other", "aboutYou")}</SelectItem>
              <SelectItem value="Prefer not to say">
                {t("preferNotToSay", "aboutYou")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          How do you want to ride?
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => vm.setSelectedRole("rider")}
            className={`p-4 rounded-xl border-2 transition-all ${
              vm.selectedRole === "rider"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300 bg-gray-50"
            }`}
          >
            <Search className="w-8 h-8 mx-auto mb-2 text-gray-700" />
            <div className="font-semibold text-gray-900 text-sm">As a Guest</div>
            <div className="text-xs text-gray-600">book a ride</div>
          </button>
          <button
            onClick={() => vm.setSelectedRole("traveller")}
            className={`p-4 rounded-xl border-2 transition-all ${
              vm.selectedRole === "traveller"
                ? "border-green-500 bg-green-50"
                : "border-gray-200 hover:border-gray-300 bg-gray-50"
            }`}
          >
            <Car className="w-8 h-8 mx-auto mb-2 text-gray-700" />
            <div className="font-semibold text-gray-900 text-sm">As a Host</div>
            <div className="text-xs text-gray-600">share a ride</div>
          </button>
          <button
            onClick={() => vm.setSelectedRole("both")}
            className={`p-4 rounded-xl border-2 transition-all ${
              vm.selectedRole === "both"
                ? "border-purple-500 bg-purple-50"
                : "border-gray-200 hover:border-gray-300 bg-gray-50"
            }`}
          >
            <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center">
              <Users className="w-8 h-8 text-gray-700" />
            </div>
            <div className="font-semibold text-gray-900 text-sm">Both</div>
            <div className="text-xs text-gray-600 leading-tight">book a ride</div>
            <div className="text-xs text-gray-600 leading-tight">
              and share a ride
            </div>
          </button>
        </div>
      </div>

      <div>
        <Label htmlFor="referredPhoneNumber">
          Enter Referred phoneNumber ({t("optional", "common")})
        </Label>
        <div className="flex w-full">
          <div className="relative z-10" ref={vm.referredCountryDropdownRef}>
            <button
              type="button"
              className="flex items-center gap-2 pl-3 pr-6 py-3 h-12 border border-gray-300 border-r-0 bg-white text-gray-700 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() =>
                vm.setReferredCountryDropdownOpen(!vm.referredCountryDropdownOpen)
              }
              tabIndex={0}
              style={{ minWidth: 90, maxWidth: 140 }}
            >
              {vm.referredSelectedCountry && (
                <>
                  <span className="font-semibold">
                    {vm.referredSelectedCountry.callingCode}
                  </span>
                  <img
                    src={`https://flagcdn.com/24x18/${vm.referredSelectedCountry.code.toLowerCase()}.png`}
                    alt="flag"
                    className="inline-block ml-2 w-5 h-4 object-cover rounded-sm border border-gray-200 bg-white"
                  />
                </>
              )}
            </button>
            {vm.referredCountryDropdownOpen && (
              <div
                className="absolute left-0 right-0 w-[320px] z-50 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-y-auto overflow-x-auto animate-fade-in"
                style={{ maxHeight: "200px" }}
              >
                <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search country or code"
                    value={vm.referredCountrySearch}
                    onChange={(e) => vm.setReferredCountrySearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
                <ul className="divide-y divide-gray-100">
                  {vm.filteredReferredCountries.length === 0 && (
                    <li className="p-4 text-gray-500 text-center">
                      No countries found
                    </li>
                  )}
                  {vm.filteredReferredCountries.slice(0, 20).map((c) => (
                    <li
                      key={c.callingCode}
                      className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                        vm.referredCountryCode === c.callingCode
                          ? "bg-blue-100"
                          : ""
                      }`}
                      onClick={() => vm.handleReferredCountrySelect(c)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          vm.handleReferredCountrySelect(c);
                        }
                      }}
                    >
                      <span className="font-semibold">{c.callingCode}</span>
                      <img
                        src={`https://flagcdn.com/24x18/${c.code.toLowerCase()}.png`}
                        alt={c.name}
                        className="inline-block ml-2 w-5 h-4 object-cover rounded-sm border border-gray-200 bg-white"
                      />
                      <span className="ml-2">{c.name}</span>
                    </li>
                  ))}
                  {vm.filteredReferredCountries.length > 20 && (
                    <li className="p-2 text-xs text-gray-400 text-center">
                      Showing first 20 results. Refine your search for more.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <input
            id="referredPhoneNumber"
            type="tel"
            placeholder="Enter referred phone number"
            value={vm.referredPhoneNumber}
            onChange={(e) => vm.setReferredPhoneNumber(e.target.value)}
            maxLength={10}
            onBlur={() => vm.validateReferredPhoneNumber()}
            className={`flex-1 h-12 py-3 px-4 border border-gray-300 border-l-0 rounded-r-xl focus:outline-none focus:ring-2 ${
              vm.referredPhoneNumberError
                ? "border-red-500 focus:ring-red-500"
                : "focus:ring-blue-500"
            }`}
            style={{ minWidth: 0 }}
          />
        </div>
        {vm.referredPhoneNumberError && (
          <p className="mt-1 text-sm text-red-600">
            {vm.referredPhoneNumberError}
          </p>
        )}
      </div>

      <Button
        onClick={vm.handleRoleSubmit}
        disabled={vm.loading || !vm.firstName || !vm.lastName}
        className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 py-3"
      >
        {vm.loading ? "Registering..." : "Complete Registration"}
        {!vm.loading && <ArrowRight className="w-5 h-5 ml-2" />}
      </Button>
    </div>
  );
};
